import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment-jalaali';

import Modal from './Modal';
import Icon from './Icon';
import { getMatchApi, getPublicMatchApi } from '../../api/match.api';
import {
  MATCH_STATUS_LABELS,
  MATCH_STATUS_COLORS,
  MATCH_TYPE_LABELS,
  GENDER_LABELS,
  AGE_GROUP_LABELS,
} from '../../utils/constants';
import { toJalaliFull } from '../../utils/jalali';
import { getFileUrl } from '../../utils/media';

/* =========================================================
   کمکی‌ها
========================================================= */

const fullName = (player) =>
  `${player?.firstName || ''} ${player?.lastName || ''}`.trim() || 'بازیکن';

function daysUntil(date) {
  const target = new Date(date);
  if (Number.isNaN(target.getTime())) return null;

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  return Math.round((target - startOfToday) / 86400000);
}

function TeamLogo({ club, size = 'h-20 w-20' }) {
  const url = getFileUrl(club?.logo);

  return (
    <div
      className={`${size} grid shrink-0 place-items-center overflow-hidden rounded-3xl bg-white/10 ring-2 ring-white/25 backdrop-blur`}
    >
      {url ? (
        <img src={url} alt={club?.name || ''} className="h-full w-full object-contain p-2" />
      ) : (
        <span className="text-2xl font-black text-white/90">
          {(club?.name || '?').trim().charAt(0)}
        </span>
      )}
    </div>
  );
}

function InfoItem({ icon, label, value }) {
  if (value === undefined || value === null || value === '') return null;

  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-3">
      <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-emerald-100 text-emerald-600">
        <Icon name={icon} className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-bold text-slate-400">{label}</div>
        <div className="mt-0.5 break-words text-sm font-black text-slate-700">{value}</div>
      </div>
    </div>
  );
}

function SummaryChip({ emoji, label, value, tone }) {
  return (
    <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${tone}`}>
      <span className="text-2xl leading-none">{emoji}</span>
      <div>
        <div className="text-xl font-black leading-6">{value}</div>
        <div className="text-[11px] font-bold opacity-70">{label}</div>
      </div>
    </div>
  );
}

function PlayerAvatar({ player }) {
  const url = getFileUrl(player?.photo);

  return (
    <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200">
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="text-xs font-black text-slate-400">
          {(player?.firstName || '?').charAt(0)}
        </span>
      )}
    </div>
  );
}

function EventsColumn({ title, club, stats }) {
  const rows = stats.filter(
    (s) => s.goals > 0 || s.assists > 0 || s.yellowCards > 0 || s.redCards > 0
  );

  return (
    <div className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2 border-b border-slate-100 pb-3">
        <span className="h-2 w-2 rounded-full bg-emerald-500" />
        <h4 className="truncate text-sm font-black text-slate-700">
          {title}: {club?.name || '—'}
        </h4>
      </div>

      {rows.length === 0 ? (
        <p className="py-4 text-center text-xs text-slate-400">رویدادی برای این تیم ثبت نشده است</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((s) => (
            <li
              key={s.id ?? `${s.playerId}`}
              className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <PlayerAvatar player={s.player} />
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold text-slate-700">{fullName(s.player)}</div>
                  {s.player?.jerseyNumber != null && (
                    <div className="text-[10px] text-slate-400">شماره {s.player.jerseyNumber}</div>
                  )}
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap justify-end gap-1 text-xs font-black">
                {s.goals > 0 && (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700">⚽ {s.goals}</span>
                )}
                {s.assists > 0 && (
                  <span className="rounded-full bg-sky-100 px-2 py-0.5 text-sky-700">🎯 {s.assists}</span>
                )}
                {s.yellowCards > 0 && (
                  <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-yellow-700">🟨 {s.yellowCards}</span>
                )}
                {s.redCards > 0 && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-red-700">🟥 {s.redCards}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* =========================================================
   مودال اصلی
   props:
     open, onClose
     matchId           شناسه مسابقه
     initialMatch      داده‌ی موجود در لیست (برای نمایش فوری، قبل از دریافت جزئیات کامل)
     isPublic          برای صفحات عمومی از API عمومی استفاده می‌شود
     isAdmin           یادداشت داخلی داور فقط برای مدیر سیستم نمایش داده می‌شود
========================================================= */

export default function MatchDetailsModal({
  open,
  onClose,
  matchId,
  initialMatch = null,
  isPublic = false,
  isAdmin = false,
}) {
  const [match, setMatch] = useState(initialMatch);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;

    setMatch(initialMatch);
    setError('');

    const id = matchId ?? initialMatch?.id;
    if (!id) return undefined;

    let cancelled = false;
    setLoading(true);

    (isPublic ? getPublicMatchApi(id) : getMatchApi(id))
      .then(({ data }) => {
        if (!cancelled && data?.data) setMatch(data.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'دریافت جزئیات کامل مسابقه ناموفق بود');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // initialMatch عمداً در وابستگی‌ها نیست تا با هر رندر والد دوباره درخواست نرود
  }, [open, matchId, isPublic]);

  const stats = useMemo(() => match?.playerStatistics || [], [match]);

  const homeStats = useMemo(
    () => stats.filter((s) => (s.clubId ?? s.player?.clubId) === match?.homeClubId),
    [stats, match]
  );
  const awayStats = useMemo(
    () => stats.filter((s) => (s.clubId ?? s.player?.clubId) === match?.awayClubId),
    [stats, match]
  );

  const totals = useMemo(
    () =>
      stats.reduce(
        (acc, s) => ({
          goals: acc.goals + (s.goals || 0),
          assists: acc.assists + (s.assists || 0),
          yellow: acc.yellow + (s.yellowCards || 0),
          red: acc.red + (s.redCards || 0),
        }),
        { goals: 0, assists: 0, yellow: 0, red: 0 }
      ),
    [stats]
  );

  if (!open) return null;

  const isPlayed = match?.status === 'PLAYED' && match?.result;
  const homeScore = match?.result?.homeScore ?? 0;
  const awayScore = match?.result?.awayScore ?? 0;
  const winnerId = match?.result?.winnerClubId;
  const remainingDays = match?.status === 'SCHEDULED' ? daysUntil(match?.date) : null;

  const weekday = match?.date ? moment(match.date).format('dddd') : '';
  const dateText = match?.date
    ? `${weekday ? `${weekday} ` : ''}${toJalaliFull(match.date)}`
    : '';

  const statusClass = MATCH_STATUS_COLORS[match?.status] || 'bg-slate-100 text-slate-700';

  const competition = [match?.tournament?.name, match?.stage?.name, match?.group?.name, match?.round]
    .filter(Boolean)
    .join(' • ');

  return (
    <Modal open={open} onClose={onClose} title="جزئیات کامل مسابقه" size="full">
      {!match ? (
        <div className="py-16 text-center text-sm text-slate-400">
          {loading ? 'در حال دریافت اطلاعات مسابقه...' : error || 'اطلاعاتی برای نمایش وجود ندارد'}
        </div>
      ) : (
        <div className="space-y-6">
          {/* ================= SCOREBOARD ================= */}
          <section className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-emerald-700 via-emerald-800 to-slate-900 p-5 text-white shadow-xl shadow-emerald-900/20 sm:p-8">
            <div className="pointer-events-none absolute -left-16 -top-16 h-56 w-56 rounded-full bg-emerald-400/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -right-10 h-64 w-64 rounded-full bg-teal-300/10 blur-3xl" />

            <div className="relative flex flex-wrap items-center justify-center gap-2 text-center">
              <span className={`rounded-full px-3 py-1 text-xs font-black ${statusClass}`}>
                {MATCH_STATUS_LABELS[match.status] || match.status}
              </span>
              {competition && (
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-white/90 ring-1 ring-white/20">
                  🏆 {competition}
                </span>
              )}
            </div>

            <h3 className="relative mt-3 text-center text-lg font-black sm:text-xl">{match.title}</h3>

            <div className="relative mt-6 grid grid-cols-[1fr_auto_1fr] items-start gap-3 sm:gap-6">
              {/* میزبان */}
              <div className="flex flex-col items-center gap-3 text-center">
                <TeamLogo club={match.homeClub} />
                <div className="text-sm font-black sm:text-base">{match.homeClub?.name || 'میزبان'}</div>
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold text-white/70">میزبان</span>
                {isPlayed && (
                  <div
                    className={`text-5xl font-black tabular-nums sm:text-6xl ${
                      winnerId === match.homeClubId ? 'text-emerald-300' : 'text-white'
                    }`}
                  >
                    {homeScore}
                  </div>
                )}
              </div>

              {/* وسط */}
              <div className="flex min-w-[84px] flex-col items-center gap-2 pt-6 text-center">
                <div className="text-3xl font-black text-white/40">{isPlayed ? '—' : 'VS'}</div>
                {match.time && (
                  <div className="rounded-full bg-white/10 px-3 py-1 text-xs font-black ring-1 ring-white/20">
                    ⏰ {match.time}
                  </div>
                )}
                {isPlayed && winnerId == null && (
                  <div className="rounded-full bg-amber-400/20 px-3 py-1 text-xs font-black text-amber-200">
                    مساوی
                  </div>
                )}
              </div>

              {/* مهمان */}
              <div className="flex flex-col items-center gap-3 text-center">
                <TeamLogo club={match.awayClub} />
                <div className="text-sm font-black sm:text-base">{match.awayClub?.name || 'مهمان'}</div>
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold text-white/70">مهمان</span>
                {isPlayed && (
                  <div
                    className={`text-5xl font-black tabular-nums sm:text-6xl ${
                      winnerId === match.awayClubId ? 'text-emerald-300' : 'text-white'
                    }`}
                  >
                    {awayScore}
                  </div>
                )}
              </div>
            </div>

            {remainingDays !== null && (
              <div className="relative mt-5 text-center text-xs font-bold text-white/80">
                {remainingDays > 1 && `${remainingDays} روز دیگر تا شروع بازی`}
                {remainingDays === 1 && 'بازی فردا برگزار می‌شود'}
                {remainingDays === 0 && 'بازی امروز برگزار می‌شود'}
                {remainingDays < 0 && 'زمان این بازی گذشته و هنوز نتیجه‌ای ثبت نشده است'}
              </div>
            )}
          </section>

          {error && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-bold text-amber-700">
              {error} — اطلاعات نمایش‌داده‌شده ممکن است ناقص باشد.
            </div>
          )}

          {/* ================= SUMMARY ================= */}
          {isPlayed && (
            <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <SummaryChip emoji="⚽" label="مجموع گل‌ها" value={homeScore + awayScore} tone="bg-emerald-50 text-emerald-700" />
              <SummaryChip emoji="🎯" label="پاس گل" value={totals.assists} tone="bg-sky-50 text-sky-700" />
              <SummaryChip emoji="🟨" label="کارت زرد" value={totals.yellow} tone="bg-yellow-50 text-yellow-700" />
              <SummaryChip emoji="🟥" label="کارت قرمز" value={totals.red} tone="bg-red-50 text-red-700" />
            </section>
          )}

          {/* ================= INFO ================= */}
          <section>
            <h4 className="mb-3 text-sm font-black text-slate-700">اطلاعات مسابقه</h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <InfoItem icon="calendar" label="تاریخ برگزاری" value={dateText} />
              <InfoItem icon="clock" label="ساعت شروع" value={match.time} />
              <InfoItem icon="location" label="محل برگزاری" value={match.location} />
              <InfoItem icon="ball" label="نوع مسابقه" value={MATCH_TYPE_LABELS[match.type] || match.type} />
              <InfoItem icon="users" label="رده" value={GENDER_LABELS[match.gender] || match.gender} />
              <InfoItem icon="award" label="گروه سنی" value={AGE_GROUP_LABELS[match.ageGroup]} />
              <InfoItem icon="schedule" label="فصل" value={match.season} />
              <InfoItem icon="trophy" label="تورنمنت" value={match.tournament?.name} />
              <InfoItem icon="competition" label="مرحله / گروه / دور" value={[match.stage?.name, match.group?.name, match.round].filter(Boolean).join(' • ')} />
              <InfoItem icon="userCircle" label="داور مسابقه" value={match.referee?.fullName} />
            </div>
          </section>

          {/* ================= EVENTS ================= */}
          {isPlayed && (
            <section>
              <h4 className="mb-3 text-sm font-black text-slate-700">رویدادهای بازی</h4>
              {stats.length === 0 ? (
                <div className="rounded-2xl bg-slate-50 px-4 py-6 text-center text-xs text-slate-400">
                  آمار بازیکنان (گلزنان و کارت‌ها) برای این مسابقه ثبت نشده است
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <EventsColumn title="میزبان" club={match.homeClub} stats={homeStats} />
                  <EventsColumn title="مهمان" club={match.awayClub} stats={awayStats} />
                </div>
              )}
            </section>
          )}

          {/* ================= REPORT ================= */}
          {match.result?.report && (
            <section className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
              <h4 className="mb-2 flex items-center gap-2 text-sm font-black text-slate-700">
                <Icon name="document" className="h-4 w-4 text-emerald-500" />
                گزارش مسابقه
              </h4>
              <p className="whitespace-pre-line text-sm leading-8 text-slate-600">{match.result.report}</p>
            </section>
          )}

          {isAdmin && match.result?.refereeNotes && (
            <section className="rounded-3xl border border-amber-100 bg-amber-50/60 p-5">
              <h4 className="mb-2 flex items-center gap-2 text-sm font-black text-amber-700">
                <Icon name="warning" className="h-4 w-4" />
                یادداشت داخلی داور (فقط برای مدیر سیستم)
              </h4>
              <p className="whitespace-pre-line text-sm leading-8 text-amber-900/80">{match.result.refereeNotes}</p>
            </section>
          )}

          {loading && (
            <div className="text-center text-[11px] text-slate-400">در حال به‌روزرسانی اطلاعات...</div>
          )}
        </div>
      )}
    </Modal>
  );
}
